import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, Target, Award, Users } from 'lucide-react';
import SEO from '../components/SEO';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Background images from gallery
const bgImages = [
  '/gallery/lab_13.jpg',
  '/gallery/lab_14.jpg',
  '/gallery/lab_15.jpg',
  '/gallery/lab_16.jpg',
  '/gallery/lab_17.jpg',
  '/gallery/lab_18.jpg',
];

const About = () => {
  const [about, setAbout] = useState(null);
  const [valueProposition, setValueProposition] = useState(null);
  const [currentBgIndex, setCurrentBgIndex] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-rotate background images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prev) => (prev + 1) % bgImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [aboutRes, vpRes] = await Promise.all([
        axios.get(`${API_URL}/about`),
        axios.get(`${API_URL}/value-proposition`),
      ]);
      setAbout(aboutRes.data);
      setValueProposition(vpRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  if (!about) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen pt-32 pb-24">
      <SEO 
        title="About Us - UAV Geo Solutions | Leading Technology Lab Provider"
        description="Learn about UAV Geo Solutions - India's premier provider of technology lab solutions. We transform institutions into future-ready technology centers."
        keywords="about UAV Geo Solutions, drone lab provider, technology education India, drone training company"
        url="/about"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-7xl font-heading font-bold uppercase tracking-tighter mb-6 text-glow">
            About Us
          </h1>
          <p className="text-base md:text-lg text-gray-400 font-light leading-relaxed max-w-3xl mx-auto">
            Leading the transformation of educational institutions into technology powerhouses
          </p>
        </motion.div>

        {/* Company Overview with Background Slider */}
        <section className="mb-24 relative min-h-[600px] rounded-3xl overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0">
            {bgImages.map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentBgIndex ? 1 : 0 }}
                transition={{ duration: 1.5 }}
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${img})` }}
              />
            ))}
            <div className="absolute inset-0 bg-black/60" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-indigo-500/10" />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative z-20 glass-heavy rounded-3xl p-8 md:p-12 m-8"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-semibold uppercase mb-8">{about.title}</h2>
            <p className="text-base md:text-lg text-gray-300 font-light leading-relaxed mb-8">{about.overview}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <Target size={48} className="text-primary mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-heading font-semibold mb-2">Our Mission</h3>
                <p className="text-sm text-gray-400">{about.mission}</p>
              </div>
              <div className="text-center">
                <Award size={48} className="text-primary mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-heading font-semibold mb-2">Our Expertise</h3>
                <p className="text-sm text-gray-400">
                  Specialized in Robotics, IoT, AI, and Drone Technologies with deep technical knowledge
                </p>
              </div>
              <div className="text-center">
                <Users size={48} className="text-primary mx-auto mb-4" strokeWidth={1.5} />
                <h3 className="text-xl font-heading font-semibold mb-2">Our Approach</h3>
                <p className="text-sm text-gray-400">
                  Turnkey solutions with long-term handholding and institutional support
                </p>
              </div>
            </div>
          </motion.div>
        </section>

        {/* What We Deliver */}
        <section className="mb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-semibold uppercase mb-6">What We Deliver</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              'Turnkey lab infrastructure',
              'Deep technical training',
              'Industrial project integration',
              'Long-term institutional handholding support',
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="glass rounded-xl p-6 flex items-start space-x-4"
              >
                <CheckCircle size={24} className="text-primary flex-shrink-0 mt-1" />
                <p className="text-base text-gray-300">{item}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Market Differentiation */}
        {valueProposition && (
          <section className="mb-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="glass-heavy rounded-3xl p-8 md:p-12 text-center"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-semibold uppercase mb-6">
                Market Differentiation
              </h2>
              <p className="text-xl md:text-2xl text-primary font-semibold mb-12">
                We are more technically competent and cost-efficient (up to 40% lower than market aggregators)
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {Object.values(valueProposition.stats).map((stat, index) => (
                  <div key={index} className="glass rounded-2xl p-6">
                    <p className="text-4xl font-code font-bold text-primary mb-2">{stat.split(' ')[0]}</p>
                    <p className="text-sm uppercase tracking-wider text-gray-400">
                      {stat.split(' ').slice(1).join(' ')}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          </section>
        )}

        {/* Company Details */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="glass rounded-3xl p-8 md:p-12"
          >
            <h2 className="text-2xl font-heading font-semibold uppercase mb-8 text-center">Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-center md:text-left">
              <div>
                <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 mb-2">Registered Name</h3>
                <p className="text-gray-300">UAV Geo Solutions (OPC) Private Limited</p>
              </div>
              <div>
                <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 mb-2">CIN</h3>
                <p className="text-gray-300">U95120BR2024OPC066916</p>
              </div>
              <div>
                <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 mb-2">GST</h3>
                <p className="text-gray-300">10AADCU4826A1Z0</p>
              </div>
              <div>
                <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-cyan-400 mb-2">Industry Focus</h3>
                <p className="text-gray-300">Robotics | IoT | Drone Technology | AI | Advanced Engineering Labs</p>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default About;
